import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";

const HERO = "https://customer-assets-jt897jd0.emergentagent.net/job_lab-portal-45/artifacts/f6f6au35_landing%20page.jpg";
const LOGO = "https://customer-assets-jt897jd0.emergentagent.net/job_lab-portal-45/artifacts/5i852irv_logo%203D%20print.png";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", vat_number: "", tax_code: "", address: "", city: "", zip_code: "", province: "", sdi_code: "", pec: "", phone: "", client_type: "laboratorio" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("session_expired") === "1") {
      toast.info("La tua sessione era scaduta: effettua nuovamente l'accesso.");
      params.delete("session_expired");
      const qs = params.toString();
      window.history.replaceState({}, "", qs ? `/login?${qs}` : "/login");
    }
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const forgotPassword = async () => {
    if (!form.email) { toast.error("Inserisci prima la tua email nel campo sopra"); return; }
    try {
      await api.post("/auth/forgot-password", { email: form.email });
      toast.success("Se l'email è registrata, riceverai un link per reimpostare la password.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(endpoint, payload);
      setUser(data);
      toast.success(mode === "login" ? "Accesso effettuato" : "Registrazione completata");
      navigate(data.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Laboratorio odontotecnico 3D" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-teal-950/85 via-teal-900/40 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div>
            <h1 className="font-heading text-5xl sm:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] drop-shadow-xl">
              Il portale ordini del tuo laboratorio
            </h1>
            <p className="mt-6 text-teal-50/95 max-w-xl leading-relaxed text-2xl">
              Carica prescrizioni, file STL, XML e immagini. Imposta le scadenze e segui lo stato di ogni lavoro in tempo reale.
            </p>
          </div>
          <div className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wide text-teal-200 leading-tight">
            <span className="block">Metodo, Formazione</span>
            <span className="block">e Supporto</span>
            <span className="block">alla Stampa 3D</span>
          </div>
        </div>
      </div>

      <div className="flex items-start justify-center p-6 sm:p-12 bg-background overflow-y-auto max-h-screen">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <img src={LOGO} alt="Officina Dentale 3D" className="w-[118%] max-w-none -ml-[9%] object-contain mix-blend-multiply mb-1" />
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">
            {mode === "login" ? "Bentornato" : "Crea il tuo account"}
          </h2>
          <p className="text-slate-500 mt-2">
            {mode === "login" ? "Accedi per gestire i tuoi ordini." : "Registrati come cliente del laboratorio."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome e cognome (referente)</Label>
                  <Input id="name" data-testid="register-name-input" value={form.name} onChange={update("name")} required placeholder="Mario Rossi" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Ragione sociale</Label>
                  <Input id="company" data-testid="register-company-input" value={form.company} onChange={update("company")} required placeholder="Studio Dentistico Rossi S.r.l." />
                </div>
                <div className="space-y-2">
                  <Label>Tipo di attività</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[["laboratorio", "Laboratorio"], ["studio", "Studio dentistico"]].map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        data-testid={`register-ctype-${val}`}
                        onClick={() => setForm({ ...form, client_type: val })}
                        className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${form.client_type === val ? "border-teal-600 bg-teal-50 text-teal-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={form.email} onChange={update("email")} required placeholder="nome@studio.it" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} data-testid="login-password-input" value={form.password} onChange={update("password")} required placeholder="••••••••" className="pr-10" />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? "Nascondi password" : "Mostra password"} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 transition-colors">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {mode === "register" && (
              <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Dati di fatturazione</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vat">Partita IVA</Label>
                    <Input id="vat" data-testid="register-vat-input" value={form.vat_number} onChange={update("vat_number")} required placeholder="IT01234567890" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cf">Codice Fiscale</Label>
                    <Input id="cf" data-testid="register-taxcode-input" value={form.tax_code} onChange={update("tax_code")} required placeholder="RSSMRA80A01F205X" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Indirizzo</Label>
                  <Input id="address" data-testid="register-address-input" value={form.address} onChange={update("address")} required placeholder="Via Roma 1" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="zip">CAP</Label>
                    <Input id="zip" data-testid="register-zip-input" value={form.zip_code} onChange={update("zip_code")} required placeholder="20100" maxLength={5} />
                  </div>
                  <div className="space-y-2 col-span-1">
                    <Label htmlFor="city">Città</Label>
                    <Input id="city" data-testid="register-city-input" value={form.city} onChange={update("city")} required placeholder="Milano" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="prov">Prov.</Label>
                    <Input id="prov" data-testid="register-province-input" value={form.province} onChange={update("province")} required placeholder="MI" maxLength={2} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sdi">Codice SDI</Label>
                    <Input id="sdi" data-testid="register-sdi-input" value={form.sdi_code} onChange={update("sdi_code")} required placeholder="0000000" maxLength={7} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pec">PEC</Label>
                    <Input id="pec" type="email" data-testid="register-pec-input" value={form.pec} onChange={update("pec")} required placeholder="studio@pec.it" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono</Label>
                  <Input id="phone" data-testid="register-phone-input" value={form.phone} onChange={update("phone")} required placeholder="+39 ..." />
                </div>
              </div>
            )}
            <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700 h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Accedi" : "Registrati"}
            </Button>
            {mode === "login" && (
              <button type="button" data-testid="forgot-password-link" onClick={forgotPassword} className="w-full text-center text-sm text-teal-600 hover:text-teal-700 mt-1">
                Password dimenticata?
              </button>
            )}
          </form>

          <p className="mt-6 text-sm text-slate-500 text-center">
            {mode === "login" ? "Non hai un account?" : "Hai già un account?"}{" "}
            <button type="button" data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-semibold text-teal-600 hover:text-teal-700">
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
